import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  updateProfile,
  User as FirebaseUser,
  UserCredential,
} from 'firebase/auth';
import { auth } from '../lib/firebase';
import { createUser, getUser, updateUser, getUserWorkspaces } from './firestore';
import { User, Workspace, WorkspaceSettings } from '../types';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../lib/firebase';

export interface RegisterData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  workspaceName: string;
}

export interface CreateWorkspaceData {
  name: string;
  slug: string;
  plan?: Workspace['plan'];
  settings: Partial<WorkspaceSettings>;
}

// Initialize Google Auth Provider
const googleProvider = new GoogleAuthProvider();

export const registerUser = async (data: RegisterData): Promise<{ user: User; workspaces: Workspace[] }> => {
  try {
    // Create Firebase user
    const userCredential: UserCredential = await createUserWithEmailAndPassword(
      auth,
      data.email,
      data.password
    );

    const firebaseUser = userCredential.user;

    // Update Firebase user profile
    await updateProfile(firebaseUser, {
      displayName: `${data.firstName} ${data.lastName}`,
    });

    // Create user document in Firestore
    const userData: Omit<User, 'id' | 'createdAt'> = {
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      role: 'owner',
    };

    await createUser(firebaseUser.uid, userData);
    
    // Get the created user (which handles the date conversion)
    const user = await getUser(firebaseUser.uid);
    if (!user) throw new Error('Failed to create user');

    // Return empty workspaces array since we're not creating a default workspace
    return { user, workspaces: [] };
  } catch (error: unknown) {
    throw new Error((error as Error).message || 'Registration failed');
  }
};

export const loginUser = async (email: string, password: string): Promise<{ user: User; workspaces: Workspace[] }> => {
  try {
    const userCredential: UserCredential = await signInWithEmailAndPassword(auth, email, password);
    const firebaseUser = userCredential.user;

    // Get user data from Firestore
    let user = await getUser(firebaseUser.uid);
    
    if (!user) {
      // Create user document if it doesn't exist (for existing Firebase users)
      const userData: Omit<User, 'id' | 'createdAt'> = {
        email: firebaseUser.email || '',
        firstName: firebaseUser.displayName?.split(' ')[0] || '',
        lastName: firebaseUser.displayName?.split(' ').slice(1).join(' ') || '',
        role: 'owner',
      };
      await createUser(firebaseUser.uid, userData);
      user = await getUser(firebaseUser.uid);
      if (!user) throw new Error('Failed to create user');
    }

    // Update last login
    await updateUser(firebaseUser.uid, { lastLogin: new Date().toISOString() });

    // Get user workspaces
    const workspaces = await getUserWorkspaces(firebaseUser.uid);

    return { user, workspaces };
  } catch (error: unknown) {
    throw new Error((error as Error).message || 'Login failed');
  }
};

export const loginWithGoogle = async (): Promise<{ user: User; workspaces: Workspace[] }> => {
  try {
    const userCredential: UserCredential = await signInWithPopup(auth, googleProvider);
    const firebaseUser = userCredential.user;

    // Check if user exists in Firestore
    let user = await getUser(firebaseUser.uid);
    
    if (!user) {
      // Create user document for new Google users
      const names = firebaseUser.displayName?.split(' ') || ['', ''];
      const userData: Omit<User, 'id' | 'createdAt'> = {
        email: firebaseUser.email || '',
        firstName: names[0] || '',
        lastName: names.slice(1).join(' ') || '',
        avatar: firebaseUser.photoURL || undefined,
        role: 'owner',
      };
      
      await createUser(firebaseUser.uid, userData);
      user = await getUser(firebaseUser.uid);
      if (!user) throw new Error('Failed to create user');
    }

    // Update last login
    await updateUser(firebaseUser.uid, { lastLogin: new Date().toISOString() });

    // Get user workspaces
    const workspaces = await getUserWorkspaces(firebaseUser.uid);

    return { user, workspaces };
  } catch (error: unknown) {
    throw new Error((error as Error).message || 'Google sign-in failed');
  }
};

export const logoutUser = async (): Promise<void> => {
  try {
    await signOut(auth);
  } catch (error: unknown) {
    throw new Error((error as Error).message || 'Logout failed');
  }
};

export const getCurrentUser = (): Promise<FirebaseUser | null> => {
  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      resolve(user);
    });
  });
};

export const subscribeToAuthChanges = (callback: (user: FirebaseUser | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

export async function getUserData(uid: string): Promise<{ user: User; workspaces: Workspace[] }> {
  try {
    // Get user document from Firestore
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const userData = userDoc.data() as User;
    // Get user's workspaces
    const workspaces = await getUserWorkspaces(uid);

    return {
      user: { ...userData, id: userDoc.id },
      workspaces
    };
  } catch (error) {
    console.error('Error getting user data:', error);
    throw error;
  }
} 